// services/v3/cache/sessionStorage.ts — session persistence in localStorage (SPEC §5)
import type { QrToken, PersistedSession } from '~/types/v3/token'
import type { SessionEndReason } from '~/types/v3/session'
import { broadcastSessionCleared } from './tabSync'

export const SESSION_KEY = 'xtable.v3.session'
export const SESSION_END_KEY = 'xtable.v3.session.end'

const hasWindow = (): boolean => typeof window !== 'undefined'

function warn(op: string, err: unknown): void {
  try {
    console.warn(`[v3/session] ${op} failed`, err)
  } catch {
    /* noop */
  }
}

function isQrToken(v: unknown): v is QrToken {
  if (typeof v !== 'object' || v === null) return false
  const t = v as Record<string, unknown>
  return typeof t.qrcodeId === 'string' && typeof t.storeId === 'string' && typeof t.table === 'string'
}

/** Reads the persisted session; null when absent or corrupt. */
export function loadSession(): PersistedSession | null {
  if (!hasWindow()) return null
  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const p = parsed as Record<string, unknown>
    if (!isQrToken(p.token)) return null
    return {
      token: p.token,
      savedAt: typeof p.savedAt === 'number' ? p.savedAt : Date.now(),
      v: 1,
    }
  } catch (err) {
    warn('loadSession', err)
    return null
  }
}

/** Persists the token and clears any previous end reason. */
export function saveSession(token: QrToken): void {
  if (!hasWindow()) return
  const payload: PersistedSession = { token, savedAt: Date.now(), v: 1 }
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(payload))
    window.localStorage.removeItem(SESSION_END_KEY)
  } catch (err) {
    warn('saveSession', err)
  }
}

/** Removes the session and records `reason`, notifying other tabs. */
export function clearSession(reason: SessionEndReason): void {
  if (!hasWindow()) return
  try {
    window.localStorage.removeItem(SESSION_KEY)
    window.localStorage.setItem(SESSION_END_KEY, reason)
  } catch (err) {
    warn('clearSession', err)
  }
  broadcastSessionCleared(reason)
}

/** The reason the session ended, if one was recorded. */
export function loadEndReason(): SessionEndReason | null {
  if (!hasWindow()) return null
  try {
    const raw = window.localStorage.getItem(SESSION_END_KEY)
    return raw ? (raw as SessionEndReason) : null
  } catch (err) {
    warn('loadEndReason', err)
    return null
  }
}
